import { ProjectInvoiceTo } from '@consigli/types';

import { ValidationResult } from '@/utils/validation';

import { CreateProjectFormData } from '../types';
import { validationRules } from './validation';

/**
 * Field requirement mapping for project forms
 */
const fieldRequirements: Record<
  keyof CreateProjectFormData,
  (data: CreateProjectFormData) => boolean
> = {
  name: () => true,
  address: () => false,
  country: () => true,
  specifications: () => false,
  constructionTypes: () => true,
  organizationId: () => true,
  billingInformation: () => false,
  billingAddress: (data) => data.invoiceTo === ProjectInvoiceTo.PROJECT,
  orgNumber: (data) => data.invoiceTo === ProjectInvoiceTo.PROJECT,
  invoiceTo: () => true,
  oracleProjectNumber: () => false,
  availableServicesOverride: () => false,
};

/**
 * Field validation context mapping
 */
const fieldContext: Record<
  keyof CreateProjectFormData,
  (data: CreateProjectFormData, existingProjectNames?: string[]) => unknown[]
> = {
  name: (_, existingProjectNames) => [existingProjectNames],
  address: () => [],
  country: () => [],
  specifications: () => [],
  constructionTypes: () => [],
  organizationId: () => [],
  billingInformation: () => [],
  billingAddress: (data) => [data.invoiceTo],
  orgNumber: (data) => [data.invoiceTo],
  invoiceTo: () => [],
  oracleProjectNumber: () => [],
  availableServicesOverride: () => [],
};

/**
 * Validate all fields in the project form
 */
export const validateProjectForm = (
  data: CreateProjectFormData,
  existingProjectNames?: string[],
): ValidationResult<CreateProjectFormData> => {
  const errors: Partial<Record<keyof CreateProjectFormData, string>> = {};

  Object.keys(data).forEach((field) => {
    const fieldKey = field as keyof CreateProjectFormData;
    const rule = validationRules[fieldKey];
    if (!rule) return;

    const isRequired = fieldRequirements[fieldKey](data);
    const hasValue = data[fieldKey] != null;

    if (isRequired || (rule.required === false && hasValue)) {
      const context = fieldContext[fieldKey](data, existingProjectNames);
      const error = rule.validate(data[fieldKey], ...context);
      if (error) {
        errors[fieldKey] = error;
      }
    }
  });

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

/**
 * Validate a single field and return its error
 */
export const validateSingleFieldProject = (
  field: keyof CreateProjectFormData,
  value: unknown,
  data: CreateProjectFormData,
  existingProjectNames?: string[],
): string | undefined => {
  const rule = validationRules[field];
  if (!rule) return undefined;

  const isRequired = fieldRequirements[field](data);
  const hasValue = value != null;

  if (isRequired || (rule.required === false && hasValue)) {
    const context = fieldContext[field](data, existingProjectNames);
    return rule.validate(value, ...context);
  }

  return undefined;
};

// Export validation rules for potential external use
export { validationRules };
